import logoSvg from "@/assets/svg/logo-icon.svg";
import { BidBattleCard } from "@/components/battle";
import { seo } from "@/lib/seo";
import { m } from "@/paraglide/messages";
import { BidBattleStatus } from "@bidbattles/bloks/components/battle";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@bidbattles/bloks/components/ui/accordion";
import { Divider } from "@bidbattles/bloks/components/ui/divider";
import { cn } from "@bidbattles/bloks/lib/ui";
import { formatCurrency } from "@bidbattles/bloks/lib/utils";
import {
	faArrowDown,
	faArrowRight,
	faCheck,
	faCheckCircle,
	faChess,
	faCircleQuestion,
	faClock,
	faCrown,
	faGift,
	faInfoCircle,
	faLightbulb,
	faTrophy,
	faUserPlus,
	faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle, Lightbulb01 } from "@untitledui/icons";

export const Route = createFileRoute("/how-it-works")({
	component: RouteComponent,
	head: () => ({
		meta: [
			...seo({
				title: "How it Works | BidBattles",
			}),
		],
	}),
});

const battleStatusCards = [
	{
		status: "LIVE",
		badge: "LIVE",
		title: "Live Battles",
		description: "All seats are taken and Currently active.",
		items: [
			"Battle in progress",
			"Timer actively counting down",
			"Players bidding in real-time",
			"Most exciting action",
		],
	},
	{
		status: "OPEN",
		badge: "OPEN",
		title: "Open Battles",
		description:
			"Waiting for seats to fill before starting. Join and get notified when it goes live!",
		items: [
			"Seats still available",
			"Countdown not started yet",
			"Entry price locked in",
			"Perfect for planning",
		],
	},
	{
		status: "FINISHED",
		badge: "Completed",
		title: "Complete Battles",
		description: "Declaration of a winner. Big rewards!",
		items: [
			"Battle is completed",
			"No more biddings",
			"Premium accounts receives un-used bids as Loyaltycoins",
			"Winner is notified",
		],
	},
] as const;

const frequentlyAskedQuestions = [
	{
		id: "not-winning",
		question: "What happens if I don't win?",
		answer:
			"Your entry price is non-refundable, as they represent your participation in the battle. However, you can immediately join other battles for another chance to win!",
	},
	{
		id: "joining-live-battle",
		question: "Can I join a battle that's already live?",
		answer:
			"No! A battle that is already live, has no seats left to join. All battles go live after all seats are taken!",
	},
	{
		id: "bid-purchase-limit",
		question: "Is there a limit to how many bids I can buy?",
		answer:
			"No limit! You can purchase as many bid packages as you want during the battle. Strategic players often buy bids in waves based on the competition.",
	},
	{
		id: "receiving-winnings",
		question: "How do I receive my winnings?",
		answer:
			"Winners receive a notification with delivery details immediately after the battle ends. Most products are shipped within 5-7 business days. Vehicles and large items may require pickup arrangements.",
	},
	{
		id: "product-authenticity",
		question: "Are the products authentic and new?",
		answer:
			"Yes! All products come directly from authorized dealers with full manufacturer warranties. Vehicles include all standard documentation and registration support. If the product has an after sales insurance, you can find it on the product information page.",
	},
] as const;

function RouteComponent() {
	return (
		<main className="flex flex-col gap-y-8 items-stretch grow h-full">
			<section className="p-box flex flex-col items-center gap-y-12 md:gap-y-16 mx-auto">
				<div className="flex flex-col gap-y-3 text-center max-w-100 md:max-w-180">
					<h1 className="text-gray-900 font-bold text-5xl leading-11 md:text-6xl md:leading-22.5">
						{m.how_it_works__hero__title()}
					</h1>
					<p className="text-gray-500 text-lg leading-7">
						{m.how_it_works__hero__subtitle()}
					</p>
				</div>
				<div className="border-2 border-gray-200 rounded-lg p-4 md:p-8 flex flex-col gap-2 md:gap-4 w-full max-w-4xl">
					<h2 className="inline-flex items-center gap-1.5 md:gap-3 text-gray-900 font-bold text-xl md:text-3xl">
						<FontAwesomeIcon icon={faLightbulb} className="text-gray-500" />
						<span>{m.how_it_works__callout_title()}</span>
					</h2>
					<p className="text-gray-500 text-sm md:text-base">
						{m.how_it_works__callout_subtitle()}
					</p>
				</div>
			</section>
			<section className="p-box flex flex-col items-center bg-gray-warm-100 gap-12">
				<div className="p-box max-w-container w-full mx-auto mb-4 md">
					<h3 className="text-gray-900 font-bold text-2xl md:text-4xl text-center">
						{m.how_it_works__process_steps_title()}
					</h3>
				</div>
				<HIWCard className="text-gray-500">
					<HIWStepNumber className="mb-4">1</HIWStepNumber>
					<HIWStepTitle className="mb-8">
						Choose Your Battle &amp; Pay Entry Price
					</HIWStepTitle>
					<div className="flex flex-col md:grid md:grid-cols-5 gap-8 md:items-center">
						<div className="flex flex-col col-span-3">
							<p className="mb-4">
								Browse available battles and select the product you want to win.
								Each battle requires an <strong>entry price</strong> to secure
								your seat (typically €30-€100 depending on the product value).
							</p>
							<ul className="flex flex-col gap-4">
								<li className="flex items-start gap-2">
									<FontAwesomeIcon
										icon={faCheckCircle}
										className="text-gray-500 mt-1"
									/>
									<p>
										<strong>Limited Seats:</strong> Battles have capped entries
										(e.g., /100, /200, /1000 seats)
									</p>
								</li>
								<li className="flex items-start gap-2">
									<FontAwesomeIcon
										icon={faCheckCircle}
										className="text-gray-500 mt-1"
									/>
									<p>
										<strong>Battle Types:</strong> Live, Open, and Premium
										battles available
									</p>
								</li>
								<li className="flex items-start gap-2">
									<FontAwesomeIcon
										icon={faCheckCircle}
										className="text-gray-500 mt-1"
									/>
									<p>
										<strong>Secure Entry:</strong> Your entry price guarantees
										your participation
									</p>
								</li>
							</ul>
						</div>
						<div className="text-gray-900 col-span-2 pb-8">
							<p className="text-center w-full font-bold">Example Battle</p>
							<BidBattleCard
								battle={{
									item_name: "Example",
									item_description: "Example",
									item_image_url: logoSvg,
									entry_price: 100,
									open_seats: 200,
									total_seats: 200,
									status: "OPEN",
								}}
								render={(props) => <div {...props} />}
							/>
						</div>
					</div>
				</HIWCard>
				<HIWCard className="text-gray-500">
					<HIWStepNumber className="mb-4">2</HIWStepNumber>
					<HIWStepTitle className="mb-4">
						Purchase Your Bid Packages
					</HIWStepTitle>
					<p className="text-center mb-6">
						Each bid has a cost and adds time to the battle timer. Choose your
						strategy wisely!
					</p>
					<div className="flex flex-col gap-4 md:flex-row md:justify-center w-full mb-8 md:mb-12">
						<HIWBidPriceCard seconds={0} price={30 * 100} />
						<HIWBidPriceCard
							seconds={3}
							price={10 * 100}
							className="scale-105 border-accent-primary shadow-lg"
						/>
						<HIWBidPriceCard seconds={5} price={6 * 100} />
					</div>
					<div className="w-full">
						<p className="inline-flex items-center gap-2 text-gray-900 w-full mb-3">
							<FontAwesomeIcon
								icon={faInfoCircle}
								className="text-accent-primary"
							/>
							<strong>Strategic Bidding Tips</strong>
						</p>
						<ul className="flex flex-col gap-2">
							<li>
								<strong>+0s bids:</strong> Use for last-second attacks when you
								want to bid without giving opponents time
							</li>
							<li>
								<strong>+3s bids:</strong> Perfect for maintaining pressure
								throughout the battle
							</li>
							<li>
								<strong>+5s bids:</strong> Most economical for extending battles
								and testing opponents
							</li>
						</ul>
					</div>
				</HIWCard>
				<HIWCard className="text-gray-500">
					<HIWStepNumber className="mb-4">3</HIWStepNumber>
					<HIWStepTitle className="mb-8">
						Place Your Strategic Bids During Live Auction
					</HIWStepTitle>
					<div className="flex flex-col md:grid md:grid-cols-2 gap-4 md:gap-8 md:items-center">
						<div className="flex flex-col">
							<p className="mb-4">
								Once the battle is live, use your purchased bids strategically.
								Each bid you place:
							</p>
							<ul className="flex flex-col gap-4">
								<li className="flex gap-2 items-start">
									<FontAwesomeIcon
										icon={faClock}
										className="text-gray-500 mt-1"
									/>
									<p>
										<strong className="block">Extends the battle timer</strong>
										<span className="block">
											Adds time based on your bid type:
										</span>
										(+0s, +3s or +5s)
									</p>
								</li>
								<li className="flex gap-2 items-start">
									<FontAwesomeIcon
										icon={faCrown}
										className="text-gray-500 mt-1"
									/>
									<p>
										<strong className="block">
											Makes you the current last bidder
										</strong>
										Your name appears on top of the leaderboard
									</p>
								</li>
							</ul>
						</div>
						<div className="pb-8 flex flex-col gap-4">
							<p className="text-center w-full font-bold text-gray-900">
								Live battle timer
							</p>
							<div className="p-5 text-center w-full rounded-[0.625rem] border border-gray-300">
								<p className="font-mono text-gray-900 text-3xl md:text-4xl">
									00:00:15
								</p>
							</div>
							<p className="text-sm text-gray-500 text-center">
								Timer resets/extends with each bid placed
							</p>
							<div className="bg-gray-200 rounded-lg pt-6 px-4 pb-4 flex flex-col gap-3">
								<p className="text-gray-900">
									<strong>Current Bidders</strong>
								</p>
								<ul className="flex flex-col gap-2">
									<li className="bg-[#FF8C42] text-gray-900 p-3 rounded-lg">
										<p className="inline-flex gap-2 items-center w-full">
											<FontAwesomeIcon icon={faTrophy} />
											<b>Max Zilver</b>
											<span className="ms-auto">+3s</span>
										</p>
									</li>
									<li className="bg-white p-3 rounded-lg">
										<p className="inline-flex gap-2 items-center w-full">
											<b>Andre F.</b>
											<span className="ms-auto">+1s</span>
										</p>
									</li>
									<li className="bg-white p-3 rounded-lg">
										<p className="inline-flex gap-2 items-center w-full">
											<b>Bart L.</b>
											<span className="ms-auto">+0s</span>
										</p>
									</li>
								</ul>
							</div>
						</div>
					</div>
					<div className="w-full">
						<p className="inline-flex items-center gap-2 text-gray-900 w-full mb-3">
							<FontAwesomeIcon icon={faChess} className="text-gray-500" />
							<strong>Strategic Considerations</strong>
						</p>
						<ul className="flex flex-col md:grid md:grid-cols-2 text-sm gap-2">
							<li>
								<strong>Timing is Everything:</strong> Wait for the right moment
								to place bids
							</li>
							<li>
								<strong>Monitor Opponents:</strong> Watch how many bids others
								have remaining
							</li>
							<li>
								<strong>Budget Management:</strong> Don't exhaust all bids too
								early
							</li>
							<li>
								<strong>Last-Second Attacks:</strong> Save +0s bids for the
								final moments
							</li>
						</ul>
					</div>
				</HIWCard>
				<HIWCard className="text-gray-500">
					<HIWStepNumber className="mb-4">4</HIWStepNumber>
					<HIWStepTitle className="mb-8">
						When the timer reaches 0 and your bid is on top!
					</HIWStepTitle>
					<p className="font-mono text-gray-900 text-3xl md:text-4xl mb-4">
						00:00:15
					</p>
					<p className="text-4xl md:text-7xl text-gray-900 mb-6">
						<FontAwesomeIcon icon={faTrophy} />
					</p>
					<p className="text-2xl md:text-3xl text-gray-900 text-center">
						<strong>Congratulations “Your name”!</strong>
					</p>
					<p className="text-center mb-8">You're the winner!</p>
					<div className="flex flex-col md:grid md:grid-cols-2 gap-4 md:gap-8 w-full">
						<div className="bg-gray-200 rounded-lg pt-6 px-4 pb-4 flex flex-col gap-3">
							<p className="text-gray-900 inline-flex items-center gap-2">
								<FontAwesomeIcon
									icon={faCheckCircle}
									className="text-gray-500"
								/>
								<strong>What Happens When You Win</strong>
							</p>
							<ul className="flex flex-col gap-2 text-sm">
								<li className="flex items-start gap-2">
									<FontAwesomeIcon
										aria-hidden
										icon={faCheck}
										className="mt-1 shrink-0"
									/>
									Timer hits 00:00:00 with your bid on top
								</li>
								<li className="flex items-start gap-2">
									<FontAwesomeIcon
										aria-hidden
										icon={faCheck}
										className="mt-1 shrink-0"
									/>
									Battle ends immediately
								</li>
								<li className="flex items-start gap-2">
									<FontAwesomeIcon
										aria-hidden
										icon={faCheck}
										className="mt-1 shrink-0"
									/>
									You're declared the winner
								</li>
								<li className="flex items-start gap-2">
									<FontAwesomeIcon
										aria-hidden
										icon={faCheck}
										className="mt-1 shrink-0"
									/>
									Product is reserved in your dashboard for you
								</li>
								<li className="flex items-start gap-2">
									<FontAwesomeIcon
										aria-hidden
										icon={faCheck}
										className="mt-1 shrink-0"
									/>
									If not complete, fill in your delivery details
								</li>
							</ul>
						</div>
						<div className="bg-gray-200 rounded-lg pt-6 px-4 pb-4 flex flex-col gap-3">
							<p className="text-gray-900 inline-flex items-center gap-2">
								<FontAwesomeIcon icon={faGift} className="text-gray-500" />
								<strong>Your Total Investment</strong>
							</p>
							<div className="grid grid-cols-2 gap-3 text-sm">
								<p>Entry price (example):</p>
								<p className="text-end font-bold">
									{formatCurrency("EUR", 100 * 100)}
								</p>
								<p>Bids used (example):</p>
								<p className="text-end font-bold">
									{formatCurrency("EUR", 25 * 100)}
								</p>
							</div>
							<Divider />
							<div className="flex items-center justify-between gap-4 text-gray-900">
								<p className="font-bold">Total Spent:</p>
								<p className="font-bold text-accent-primary">
									{formatCurrency("EUR", 125 * 100)}
								</p>
							</div>
							<div className="rounded bg-white p-3">
								<p className="text-xs text-gray-500">Product Value:</p>
								<p className="text-2xl font-bold text-accent-primary">
									{formatCurrency("EUR", 50_000 * 100)}
								</p>
							</div>
							<p className="text-center text-xs font-bold text-accent-primary">
								You saved {formatCurrency("EUR", 49_875 * 100)}!
							</p>
						</div>
					</div>
				</HIWCard>
				<HIWCard className="text-gray-500">
					<HIWStepTitle className="mb-8">
						Understanding Capped-Player Auctions
					</HIWStepTitle>

					<p className="text-gray-900 text-xl md:text-2xl mb-6">
						<strong>Seat Limitations Explained</strong>
					</p>

					<div className="w-full flex flex-col gap-4">
						<p className="text-gray-900">
							<strong>Example: 200-seat battle</strong>
						</p>

						<div className="flex flex-col gap-4 md:grid md:grid-cols-2">
							<div className="flex flex-col gap-3">
								<p className="text-sm">Seat status:</p>
								<div className="grid grid-cols-9 gap-3 text-sm md:text-base">
									<p className="aspect-square w-full bg-accent-primary rounded-lg text-white font-semibold flex items-center justify-center text-center">
										1
									</p>
									<p className="aspect-square w-full bg-accent-primary rounded-lg text-white font-semibold flex items-center justify-center text-center">
										2
									</p>
									<p className="aspect-square w-full bg-accent-primary rounded-lg text-white font-semibold flex items-center justify-center text-center">
										3
									</p>
									<p className="aspect-square w-full bg-accent-primary rounded-lg text-white font-semibold flex items-center justify-center text-center">
										...
									</p>
									<p className="aspect-square w-full bg-accent-primary rounded-lg text-white font-semibold flex items-center justify-center text-center">
										95
									</p>
									<p className="aspect-square w-full bg-gray-200 rounded-lg text-gray-400 font-semibold flex items-center justify-center text-center">
										96
									</p>
									<p className="aspect-square w-full bg-gray-200 rounded-lg text-gray-400 font-semibold flex items-center justify-center text-center">
										97
									</p>
									<p className="aspect-square w-full bg-gray-200 rounded-lg text-gray-400 font-semibold flex items-center justify-center text-center">
										...
									</p>
									<p className="aspect-square w-full bg-gray-200 rounded-lg text-gray-400 font-semibold flex items-center justify-center text-center">
										200
									</p>
								</div>
								<div className="bg-gray-200 rounded-lg pt-6 px-4 pb-4 flex flex-col gap-3">
									<p className="text-accent-primary inline-flex items-center gap-2">
										<FontAwesomeIcon
											icon={faUserPlus}
											className="text-gray-500"
										/>
										<strong>105 Seats still open</strong>
									</p>
									<p>Still available for new players to join</p>
								</div>
							</div>
							<div className="md:self-end">
								<div className="bg-gray-200 rounded-lg pt-6 px-4 pb-4 flex flex-col gap-3">
									<p className="text-gray-900 inline-flex items-center gap-2">
										<FontAwesomeIcon icon={faUsers} className="text-gray-500" />
										<strong>95 Seats are taken</strong>
									</p>
									<p>Players who paid entry price and are competing</p>
								</div>
							</div>
						</div>
					</div>
					{/**
					<p className="text-gray-900">
						<FontAwesomeIcon icon={faInfoCircle} className="text-gray-500" />
						<strong>Why Capped Players?</strong>
					</p>
          <ul className="flex flex-col"></ul>
          */}
				</HIWCard>
				<HIWCard className="text-gray-500">
					<HIWStepTitle className="mb-8">
						How the Battle Timer Works
					</HIWStepTitle>
					<div className="w-full flex flex-col gap-3">
						<p className="text-gray-900">
							<strong>The Countdown System</strong>
						</p>
						<p>
							The battle timer creates thrilling last-second competition. Here's
							exactly how it works:
						</p>
						<div className="bg-gray-200 rounded-lg p-6 grid grid-rows-3 md:grid-rows-1 md:grid-cols-3 mb-2">
							<div className="text-center flex flex-col p-2 gap-3">
								<p className="font-mono text-gray-900 text-xl md:text-2xl grow flex items-center justify-center">
									00:00:45
								</p>
								<p className="text-sm">Battle countdown active</p>
							</div>
							<div className="flex justify-center items-center text-accent-primary text-2xl md:text-3xl p-4">
								<FontAwesomeIcon icon={faArrowDown} className="md:hidden!" />
								<FontAwesomeIcon
									icon={faArrowRight}
									className="hidden! md:inline-block!"
								/>
							</div>
							<div className="text-center flex flex-col p-2 gap-3">
								<p className="font-mono text-gray-900 text-xl md:text-2xl grow flex items-center justify-center">
									00:00:48
								</p>
								<p className="text-sm">
									Player bids (<span className="text-accent-primary">+3s</span>)
									Timer extends!
								</p>
							</div>
						</div>
						<ul className="flex flex-col gap-4">
							<li className="flex items-start gap-2 md:gap-4">
								<div className="size-6 md:size-8 text-sm md:text-base rounded-full bg-gray-500 text-white flex items-center justify-center text-center shrink-0">
									1
								</div>
								<div>
									<p className="text-gray-900">
										<strong>Battle Starts</strong>
									</p>
									<p>
										Timer begins counting down when the battle seats are all
										taken
									</p>
								</div>
							</li>
							<li className="flex items-start gap-2 md:gap-4">
								<div className="size-6 md:size-8 text-sm md:text-base rounded-full bg-gray-500 text-white flex items-center justify-center text-center shrink-0">
									2
								</div>
								<div>
									<p className="text-gray-900">
										<strong>Players Place Bids</strong>
									</p>
									<p>
										When someone bids, the timer extends based on bid type (+0s,
										+3s, or +5s)
									</p>
								</div>
							</li>
							<li className="flex items-start gap-2 md:gap-4">
								<div className="size-6 md:size-8 text-sm md:text-base rounded-full bg-gray-500 text-white flex items-center justify-center text-center shrink-0">
									3
								</div>
								<div>
									<p className="text-gray-900">
										<strong>Continuous Extension</strong>
									</p>
									<p>
										Each new bid adds to the timer, keeping the battle alive
									</p>
								</div>
							</li>
							<li className="flex items-start gap-2 md:gap-4">
								<div className="size-6 md:size-8 text-sm md:text-base rounded-full bg-accent-primary text-white flex items-center justify-center text-center shrink-0">
									4
								</div>
								<div>
									<p className="text-gray-900">
										<strong>Battle Ends</strong>
									</p>
									<p>
										When timer reaches 00:00:00, the last bidder wins the
										product!
									</p>
								</div>
							</li>
						</ul>
					</div>
				</HIWCard>
				<HIWCard className="gap-8 text-gray-500 md:gap-12">
					<h2 className="text-center text-2xl font-bold text-gray-900 md:text-4xl">
						Status of Battles
					</h2>
					<div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3 md:gap-8">
						{battleStatusCards.map((statusCard) => (
							<div
								key={statusCard.status}
								className="flex min-w-0 flex-col rounded-xl border-2 border-gray-200 p-4 md:p-6"
							>
								<BidBattleStatus
									status={statusCard.status}
									getLabel={() => statusCard.badge}
									className={cn(
										"self-center text-center shadow-md",
										statusCard.status === "FINISHED" && "text-green-400",
									)}
								/>
								<h3 className="mt-4 text-center text-xl font-bold text-gray-900">
									{statusCard.title}
								</h3>
								<p className="mt-2 text-sm leading-5">
									{statusCard.description}
								</p>
								<ul className="mt-6 flex flex-col gap-2 text-sm leading-5">
									{statusCard.items.map((item) => (
										<li key={item} className="flex items-start gap-2">
											<FontAwesomeIcon
												aria-hidden
												icon={faCheck}
												className="mt-1 shrink-0 text-gray-500"
											/>
											<span>{item}</span>
										</li>
									))}
								</ul>
							</div>
						))}
					</div>
				</HIWCard>
			</section>
			<section aria-labelledby="faq-heading" className="px-box py-12 md:py-16">
				<div className="mx-auto w-full max-w-4xl">
					<h2
						id="faq-heading"
						className="mb-8 text-center text-2xl font-bold text-gray-900 md:text-4xl"
					>
						Frequently Asked Questions
					</h2>
					<Accordion className="flex flex-col gap-3">
						{frequentlyAskedQuestions.map((item) => (
							<AccordionItem
								key={item.id}
								value={item.id}
								className="rounded-lg border border-gray-200 px-4 last:border-b"
							>
								<AccordionTrigger className="font-bold text-gray-900">
									<span className="flex min-w-0 items-start gap-2">
										<FontAwesomeIcon
											aria-hidden
											icon={faCircleQuestion}
											className="mt-0.5 shrink-0 text-gray-500"
										/>
										<span>{item.question}</span>
									</span>
								</AccordionTrigger>
								<AccordionContent className="text-gray-500">
									{item.answer}
								</AccordionContent>
							</AccordionItem>
						))}
					</Accordion>
				</div>
			</section>
		</main>
	);
}

function HIWCard({
	className,
	children,
}: React.PropsWithChildren<{ className?: string }>) {
	return (
		<div
			className={cn(
				"p-4 md:p-8 w-full max-w-5xl flex flex-col items-center bg-white rounded-xl",
				className,
			)}
		>
			{children}
		</div>
	);
}

function HIWStepNumber({
	className,
	children,
}: React.PropsWithChildren<{ className?: string }>) {
	return (
		<div
			className={cn(
				"rounded-full size-15 flex items-center justify-center",
				className,
			)}
			style={{
				background: "linear-gradient(131deg, #FF7B3C 0%, #B54708 238.67%)",
			}}
		>
			<p className="font-bold text-white text-2xl">{children}</p>
		</div>
	);
}

function HIWStepTitle({
	className,
	children,
}: React.PropsWithChildren<{ className?: string }>) {
	return (
		<p
			className={cn(
				"font-bold text-lg md:text-2xl text-center text-gray-900",
				className,
			)}
		>
			{children}
		</p>
	);
}

function HIWBidPriceCard({
	price,
	seconds,
	className,
}: { price: number; seconds: number; className?: string }) {
	return (
		<div
			className={cn(
				"border-2 border-gray-200 basis-5/6 p-4 py-8 flex flex-col items-center justify-center rounded-lg md:aspect-160/187",
				className,
			)}
		>
			<p className="font-bold text-gray-900 text-xl mb-2">+{seconds}s Bid</p>
			<p className="font-bold text-accent-primary text-3xl mb-2">
				{formatCurrency("EUR", price)}
			</p>
			<p className="text-sm mb-7">per bid</p>
			<p className="text-xs mb-1">Time Extension</p>
			<p className="font-bold text-gray-900 text-2xl">+{seconds} seconds</p>
		</div>
	);
}
